import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "@/lib/supabaseClient";
import { useAuth } from "@/lib/AuthContext";
import { logActivity } from "@/lib/activityLog";
import { ModalShell, Field, TextInput, SelectInput, ModalFooter, ErrorBanner } from "../shared/FormElements";

const STATUS_OPTIONS = ["unpaid", "paid", "overdue"];

export default function Invoices() {
  const { profile } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all");
  const [showUpload, setShowUpload] = useState(false);

  useEffect(() => {
    load();
  }, []);

  async function load() {
    setLoading(true);
    const { data, error: fetchError } = await supabase
      .from("invoices")
      .select("*, clients(company_name), projects(name)")
      .order("invoice_date", { ascending: false });
    if (fetchError) setError(fetchError.message);
    setInvoices(data || []);
    setLoading(false);
  }

  const filtered = useMemo(() => {
    if (filter === "all") return invoices;
    return invoices.filter((i) => i.status === filter);
  }, [invoices, filter]);

  const kpis = useMemo(() => {
    const outstanding = invoices
      .filter((i) => i.status !== "paid")
      .reduce((sum, i) => sum + Number(i.amount), 0);
    const overdue = invoices.filter((i) => i.status === "overdue");
    const thisMonth = new Date().toISOString().slice(0, 7);
    const paidThisMonth = invoices
      .filter((i) => i.status === "paid" && i.invoice_date?.startsWith(thisMonth))
      .reduce((sum, i) => sum + Number(i.amount), 0);
    return { outstanding, overdueCount: overdue.length, paidThisMonth };
  }, [invoices]);

  async function updateStatus(id, status) {
    const invoice = invoices.find((i) => i.id === id);
    await supabase.from("invoices").update({ status }).eq("id", id);
    if (invoice) {
      logActivity({
        actorId: profile?.id,
        clientId: invoice.client_id,
        action: `Marked invoice ${status}`,
        details: invoice.invoice_number,
      });
    }
    load();
  }

  async function download(invoice) {
    if (!invoice.file_url) return;
    const { data, error: urlError } = await supabase.storage
      .from("invoices")
      .createSignedUrl(invoice.file_url, 60);
    if (urlError) {
      setError(urlError.message);
      return;
    }
    window.open(data.signedUrl, "_blank");
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <h1 className="text-xl font-semibold text-foreground">Invoices</h1>
      </div>
      <p className="text-sm text-muted-foreground mb-6">Continuous numbering, AGY-00001 onward</p>

      <ErrorBanner error={error} />

      <div className="grid grid-cols-4 gap-4 mb-6">
        <Kpi label="Outstanding" value={`₹${kpis.outstanding.toLocaleString("en-IN")}`} />
        <Kpi label="Overdue" value={kpis.overdueCount} />
        <Kpi label="Paid this month" value={`₹${kpis.paidThisMonth.toLocaleString("en-IN")}`} />
        <Kpi label="Numbering" value="AGY-00001+" />
      </div>

      <div className="flex items-center justify-between mb-4">
        <div className="flex gap-2">
          {["all", "unpaid", "paid", "overdue"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`font-mono text-[11.5px] px-3 py-1.5 rounded-full border ${
                filter === f ? "bg-primary text-primary-foreground border-primary" : "border-input text-muted-foreground"
              }`}
            >
              {f}
            </button>
          ))}
        </div>
        <button
          onClick={() => setShowUpload(true)}
          className="bg-primary text-primary-foreground rounded-lg px-4 py-2 text-sm font-semibold"
        >
          + Upload invoice
        </button>
      </div>

      <div className="border border-border rounded-2xl bg-card overflow-hidden">
        {loading ? (
          <p className="text-sm text-muted-foreground p-5">Loading...</p>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-muted-foreground p-5">No invoices here yet.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <Th>Invoice</Th>
                <Th>Client</Th>
                <Th>Project</Th>
                <Th>Date</Th>
                <Th align="right">Amount</Th>
                <Th>Status</Th>
                <Th></Th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((inv) => (
                <tr key={inv.id} className="border-b border-border last:border-0">
                  <td className="px-5 py-3.5 font-medium text-foreground">{inv.invoice_number}</td>
                  <td className="px-5 py-3.5 text-muted-foreground">{inv.clients?.company_name}</td>
                  <td className="px-5 py-3.5 text-muted-foreground">{inv.projects?.name || "-"}</td>
                  <td className="px-5 py-3.5 text-muted-foreground">{inv.invoice_date}</td>
                  <td className="px-5 py-3.5 text-right">₹{Number(inv.amount).toLocaleString("en-IN")}</td>
                  <td className="px-5 py-3.5">
                    <select
                      value={inv.status}
                      onChange={(e) => updateStatus(inv.id, e.target.value)}
                      className="font-mono text-[11px] border border-input rounded-full px-2 py-1 bg-background"
                    >
                      {STATUS_OPTIONS.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-5 py-3.5 text-right">
                    {inv.file_url && (
                      <button onClick={() => download(inv)} className="text-xs text-muted-foreground hover:text-foreground underline">
                        Download
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showUpload && (
        <UploadInvoiceModal
          onClose={() => setShowUpload(false)}
          onSaved={() => {
            setShowUpload(false);
            load();
          }}
        />
      )}
    </div>
  );
}

function Th({ children, align }) {
  return (
    <th
      className={`text-${align || "left"} font-mono text-[10.5px] uppercase tracking-wide text-muted-foreground px-5 py-3`}
    >
      {children}
    </th>
  );
}

function Kpi({ label, value }) {
  return (
    <div className="border border-border rounded-2xl bg-card p-4">
      <div className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground mb-1.5">{label}</div>
      <div className="text-lg font-semibold text-foreground">{value}</div>
    </div>
  );
}

function UploadInvoiceModal({ onClose, onSaved }) {
  const { profile } = useAuth();
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [clientId, setClientId] = useState("");
  const [projectId, setProjectId] = useState("");
  const [amount, setAmount] = useState("");
  const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().slice(0, 10));
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    supabase
      .from("clients")
      .select("id, company_name")
      .order("company_name")
      .then(({ data }) => setClients(data || []));
  }, []);

  useEffect(() => {
    if (!clientId) {
      setProjects([]);
      return;
    }
    supabase
      .from("projects")
      .select("id, name")
      .eq("client_id", clientId)
      .then(({ data }) => setProjects(data || []));
  }, [clientId]);

  async function handleSave() {
    if (!clientId || !amount || !invoiceDate) {
      setError("Client, amount, and date are required.");
      return;
    }
    setSaving(true);
    setError(null);

    // Only assign a real invoice number once we're actually committing the save,
    // so a cancelled upload never burns a number and leaves a gap in the sequence.
    const { data: invoiceNumber, error: numberError } = await supabase.rpc("next_invoice_number");
    if (numberError) {
      setSaving(false);
      setError(numberError.message);
      return;
    }

    let filePath = null;
    if (file) {
      filePath = `${clientId}/${invoiceNumber}.pdf`;
      const { error: uploadError } = await supabase.storage.from("invoices").upload(filePath, file, {
        contentType: "application/pdf",
        upsert: true,
      });
      if (uploadError) {
        setSaving(false);
        setError(uploadError.message);
        return;
      }
    }

    const { error: insertError } = await supabase.from("invoices").insert({
      client_id: clientId,
      project_id: projectId || null,
      invoice_number: invoiceNumber,
      file_url: filePath,
      amount: parseFloat(amount),
      invoice_date: invoiceDate,
      status: "unpaid",
    });

    setSaving(false);
    if (insertError) {
      setError(insertError.message);
      return;
    }
    logActivity({
      actorId: profile?.id,
      clientId,
      action: "Uploaded invoice",
      details: invoiceNumber,
    });
    onSaved();
  }

  return (
    <ModalShell title="Upload invoice" subtitle="A new invoice number is assigned automatically on save" onClose={onClose}>
      <ErrorBanner error={error} />
      <Field label="Client">
        <SelectInput value={clientId} onChange={(e) => setClientId(e.target.value)}>
          <option value="">Select a client</option>
          {clients.map((c) => (
            <option key={c.id} value={c.id}>
              {c.company_name}
            </option>
          ))}
        </SelectInput>
      </Field>
      <Field label="Project (optional)">
        <SelectInput value={projectId} onChange={(e) => setProjectId(e.target.value)} disabled={!clientId}>
          <option value="">No specific project</option>
          {projects.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </SelectInput>
      </Field>
      <Field label="Amount">
        <TextInput type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="180000" />
      </Field>
      <Field label="Invoice date">
        <TextInput type="date" value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)} />
      </Field>
      <Field label="PDF (optional)">
        <input type="file" accept="application/pdf" onChange={(e) => setFile(e.target.files[0])} className="text-sm" />
      </Field>
      <ModalFooter onClose={onClose} onSave={handleSave} saving={saving} saveLabel="Upload" />
    </ModalShell>
  );
}
